import { sel } from "../selectors";
import { DEFAULT_STAGE_VIEWPORT, SAMPLES_BASE, Scene } from "./types";

// Lesson: Your style → Report templates.
// Fixture: `yourStyle` (the demo officer with one template, "General Patrol",
// so the template tabs and "New Template" show). The scene builds a "DUI"
// template, saves it, makes it the default, then shows it pre-selected in the
// upload modal's Report Settings.
const EDITOR = (anchor: "templateHeader" | "templateBody" | "templateFooter") =>
  `${sel(anchor)} .template-tiptap-editor`;

const scene: Scene = {
  id: "report-templates",
  title: "Report templates",
  startRoute: "/preferences",
  fixture: "yourStyle",
  viewport: DEFAULT_STAGE_VIEWPORT,
  steps: [
    {
      id: "intro",
      narration:
        "A template is a header, writing instructions, and a footer that the AI applies to your draft. Build one for each kind of report, like a DUI. They live at the bottom of Report Preferences.",
      actions: [
        { type: "waitFor", target: sel("templateManager") },
        { type: "scrollIntoView", target: sel("templateManager") },
        { type: "wait", ms: 1000 },
        { type: "spotlight", target: sel("templateManager") },
        { type: "wait", ms: 6000 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "new",
      narration: "Click New Template and give it a name. I'll call this one DUI.",
      actions: [
        { type: "moveTo", target: sel("templateNew") },
        { type: "wait", ms: 500 },
        { type: "click", target: sel("templateNew") },
        { type: "waitFor", target: `${sel("templateDetails")} input` },
        { type: "wait", ms: 400 },
        // "I'll call this one DUI" starts at 2.7 s of the 4.7 s clip.
        { type: "wait", ms: 800 },
        { type: "type", target: `${sel("templateDetails")} input`, text: "DUI", replace: true, cps: 6 },
        { type: "wait", ms: 300 },
        { type: "click", target: `${sel("templateDetails")} button` },
        // A seek replays instantly: wait for the rename to land so a later
        // save does not write the old name back.
        { type: "waitFor", target: '[data-template-name="DUI"]' },
        { type: "wait", ms: 400 },
      ],
    },
    {
      id: "header",
      narration:
        "Custom Header is boilerplate that opens every report of this type. Insert AI Placeholder drops in a field, like Case Number, that shows up as a bracketed blank in the draft for you to fill.",
      actions: [
        { type: "waitFor", target: EDITOR("templateHeader") },
        { type: "click", target: EDITOR("templateHeader") },
        { type: "type", target: EDITOR("templateHeader"), text: "DUI ARREST REPORT  Case #: ", cps: 14 },
        // "Insert AI Placeholder" is said at 4.3 s, "like Case Number" at 6.9 s.
        { type: "wait", ms: 1000 },
        { type: "click", target: `${sel("templateHeader")} ${sel("templateInsert")} button` },
        { type: "waitFor", target: `${sel("templateHeader")} [data-placeholder-item="Case Number"]` },
        { type: "wait", ms: 1400 },
        { type: "click", target: `${sel("templateHeader")} [data-placeholder-item="Case Number"]` },
        { type: "wait", ms: 800 },
      ],
    },
    {
      id: "instructions",
      narration:
        "Narrative Writing Instructions tell the AI how to structure the body: which sections to include, in what order, and what to emphasize.",
      actions: [
        { type: "click", target: EDITOR("templateBody") },
        {
          type: "type",
          target: EDITOR("templateBody"),
          text: "Use these sections in order: Contact, Driving Pattern, Field Sobriety Tests, Arrest, Chemical Test. Emphasize signs of impairment.",
          cps: 22,
        },
        { type: "wait", ms: 800 },
      ],
    },
    {
      id: "footer",
      narration: "Custom Footer is the closing block. I'll add a short line for the reviewing supervisor.",
      actions: [
        { type: "click", target: EDITOR("templateFooter") },
        { type: "type", target: EDITOR("templateFooter"), text: "Reviewed by: ____________", cps: 12 },
        { type: "wait", ms: 800 },
      ],
    },
    {
      id: "save-default",
      narration:
        "Click Save Preferences. Then open the gear menu and choose Set as default, so this template is pre-selected on every new upload.",
      actions: [
        { type: "click", target: sel("preferencesSave") },
        { type: "waitFor", target: '[role="status"]' },
        { type: "wait", ms: 700 },
        { type: "scrollIntoView", target: sel("templateActions") },
        // "open the gear menu" is said at 2.0 s, "Set as default" around 4.5 s.
        { type: "click", target: `${sel("templateActions")} button` },
        { type: "wait", ms: 1000 },
        { type: "click", target: `${sel("templateActions")} [data-template-action="default"]` },
        { type: "waitFor", target: '[data-template-default="true"]' },
        { type: "wait", ms: 800 },
        // Click the active tab so the gear menu closes (it stays open while focused).
        { type: "click", target: '[data-template-default="true"]' },
        { type: "wait", ms: 700 },
      ],
    },
    {
      id: "use",
      narration:
        "To use it, start a new case and add your audio. Then open Report Settings, where the Report Template menu already has DUI selected, with a star because it's my default.",
      actions: [
        { type: "click", target: `${sel("newCaseDropdown")} button` },
        { type: "waitFor", target: sel("newCaseUpload") },
        { type: "click", target: sel("newCaseUpload") },
        { type: "waitFor", target: sel("uploadDropzone") },
        {
          type: "attachFile",
          target: `${sel("uploadDropzone")} input[type="file"]`,
          url: `${SAMPLES_BASE}/narrative-retail-theft.mp3`,
          name: "narrative-retail-theft.mp3",
          mime: "audio/mpeg",
        },
        { type: "waitFor", target: sel("uploadReportSettings") },
        { type: "wait", ms: 300 },
        // Report Settings starts expanded on a fresh modal, so it is pointed at, not clicked.
        { type: "moveTo", target: sel("uploadReportSettings") },
        { type: "waitFor", target: sel("uploadTemplate") },
        { type: "scrollIntoView", target: sel("uploadTemplate") },
        { type: "spotlight", target: sel("uploadTemplate") },
        { type: "wait", ms: 2500 },
      ],
    },
    {
      id: "outro",
      narration:
        "Pick a different one, or no template at all, for a single report. Just know the template is fixed when the case is created. To try another on the same audio, start a new case from Previous Uploads.",
      actions: [
        { type: "click", target: `${sel("uploadTemplate")} button` },
        { type: "wait", ms: 4800 },
        { type: "click", target: `${sel("uploadTemplate")} button` },
        { type: "spotlight", target: null },
        { type: "wait", ms: 800 },
      ],
    },
  ],
};

export default scene;
